// Reserves the height of the fixed site header (its previous sibling) so page content starts below it,
// and hides the header while scrolling down, revealing it again as soon as the visitor scrolls up.
const SCROLL_DELTA = 8;

class HeaderSpacer extends HTMLElement {
  #observer;
  #header;
  #lastY = 0;
  #hidden = false;
  #ticking = false;

  connectedCallback() {
    const header = this.previousElementSibling;
    if (!header) return;
    this.#header = header;

    this.#observer = new ResizeObserver(() => {
      this.style.height = `${header.offsetHeight}px`;
    });
    this.#observer.observe(header);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    header.style.setProperty('transition', reduceMotion ? 'none' : 'transform 250ms ease-out', 'important');
    header.style.setProperty('will-change', 'transform', 'important');

    this.#lastY = window.scrollY;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    header.addEventListener('focusin', this.#show);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    window.removeEventListener('scroll', this.#onScroll);
    this.#header?.removeEventListener('focusin', this.#show);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  };

  #update() {
    const header = this.#header;
    const y = Math.max(window.scrollY, 0);
    const delta = y - this.#lastY;

    // Always show near the top of the page, or while a menu/cart drawer is open.
    if (y <= header.offsetHeight || header.querySelector('dialog[open]')) {
      this.#show();
      this.#lastY = y;
      return;
    }

    if (Math.abs(delta) < SCROLL_DELTA) return;

    if (delta > 0 && !header.contains(document.activeElement)) {
      this.#hide();
    } else if (delta < 0) {
      this.#show();
    }
    this.#lastY = y;
  }

  #show = () => {
    if (!this.#hidden) return;
    this.#hidden = false;
    this.#header.style.setProperty('transform', 'translateY(0)', 'important');
  };

  #hide() {
    if (this.#hidden) return;
    this.#hidden = true;
    this.#header.style.setProperty('transform', 'translateY(-100%)', 'important');
  }
}

if (!customElements.get('s-header-spacer')) {
  customElements.define('s-header-spacer', HeaderSpacer);
}
